import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ChartBarIcon, CalendarDaysIcon, CreditCardIcon, UserGroupIcon, DocumentChartBarIcon, SparklesIcon } from '@heroicons/react/24/outline';

function Home() {
    const navigate = useNavigate();

    const handleLearnMoreClick = () => {
        navigate('/marketing');
    };

    const handleGetStartedClick = () => {
        navigate('/plans');
    };

    const features = [
        {
            icon: CalendarDaysIcon,
            title: 'Session Management',
            description: 'Create and manage training sessions with flexible scheduling and capacity control.'
        },
        {
            icon: UserGroupIcon,
            title: 'Client Token System',
            description: 'Distribute tokens to clients for flexible booking power and simplified management.'
        },
        {
            icon: DocumentChartBarIcon,
            title: 'Detailed Reports',
            description: 'Track attendance, monitor engagement, and plan your day with comprehensive reporting.'
        },
        {
            icon: CreditCardIcon,
            title: 'Payment Integration',
            description: 'Seamless GoCardless integration for automated subscription and payment management.'
        },
        {
            icon: ChartBarIcon,
            title: 'User Tracking',
            description: 'Monitor client progress, identify at-risk members, and optimize your business.'
        },
        {
            icon: SparklesIcon,
            title: 'Business Automation',
            description: 'Automated bookings, cancellations, and notifications so you can focus on training.'
        }
    ];

    return (
        <div className="min-h-screen bg-luxury-black">
            {/* Navigation */}
            <nav className="border-b border-luxury-graphite/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex-shrink-0">
                            <a href="/" className="flex items-center gap-2">
                                <img
                                    src="/img/wh-logo-20260425.png"
                                    alt="WorldsHighStreet"
                                    className="h-10 w-10 object-contain"
                                />
                                <span className="hidden sm:block text-xl font-display font-bold text-emerald-400 hover:text-emerald-300 transition-colors">
                                    WorldsHighStreet
                                </span>
                            </a>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-4">
                            <a 
                                href="/find-pt" 
                                className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium hidden sm:inline"
                            >
                                Find a PT
                            </a>
                            <a 
                                href="/login" 
                                className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium"
                            >
                                Login
                            </a>
                            <button
                                onClick={handleGetStartedClick}
                                className="btn-primary text-sm sm:text-base px-4 sm:px-6 py-2"
                            >
                                Get Started
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-transparent to-emerald-600/5"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 relative">
                    <div className="text-center max-w-4xl mx-auto">
                        {/* Prominent logo */}
                        <div className="flex justify-center mb-8">
                            <img
                                src="/img/wh-logo-20260425.png"
                                alt="WorldsHighStreet"
                                className="h-32 w-32 sm:h-40 sm:w-40 object-contain drop-shadow-[0_0_24px_rgba(52,211,153,0.35)]"
                            />
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-luxury-pearl mb-6">
                            Empower Your{' '}
                            <span className="text-emerald-400">Personal Training</span>{' '}
                            Business
                        </h1>
                        <p className="text-lg sm:text-xl text-luxury-pearl/60 mb-8 max-w-2xl mx-auto">
                            Streamline session management, automate payments, and grow your PT business with our comprehensive platform designed to make your life easier.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <button
                                onClick={handleGetStartedClick}
                                className="btn-primary text-base sm:text-lg px-8 py-3 w-full sm:w-auto flex items-center justify-center gap-2 group"
                            >
                                Start Your PT Business
                                <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button
                                onClick={handleLearnMoreClick}
                                className="border border-emerald-600/30 hover:border-emerald-600/60 bg-emerald-600/5 hover:bg-emerald-600/10 text-emerald-300 font-semibold px-8 py-3 rounded-lg transition-all duration-200 w-full sm:w-auto"
                            >
                                Learn More
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="text-center mb-12 sm:mb-16">
                    <h2 className="text-3xl sm:text-4xl font-display font-bold text-gradient-emerald mb-4">
                        Everything You Need to Succeed
                    </h2>
                    <p className="text-lg text-luxury-pearl/60 max-w-2xl mx-auto">
                        A comprehensive platform designed to simplify your personal training business operations
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {features.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <div
                                key={index}
                                className="card-luxury p-6 sm:p-8 hover:shadow-luxury-lg transition-all duration-300 group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-emerald-600/10 rounded-lg flex items-center justify-center group-hover:bg-emerald-600/20 transition-colors">
                                            <Icon className="h-6 w-6 text-emerald-300" />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-display font-semibold text-luxury-pearl mb-2 group-hover:text-emerald-300 transition-colors">
                                            {feature.title}
                                        </h3>
                                        <p className="text-luxury-pearl/60 text-sm leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Find a PT Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="card-luxury p-8 sm:p-12 text-center bg-gradient-to-br from-blue-900/10 to-emerald-900/10">
                    <h2 className="text-3xl sm:text-4xl font-display font-bold text-luxury-pearl mb-4">
                        Looking for a Personal Trainer?
                    </h2>
                    <p className="text-lg text-luxury-pearl/60 mb-8 max-w-2xl mx-auto">
                        Browse our directory of certified personal trainers and find the right PT for you in your area.
                    </p>
                    <a
                        href="/find-pt"
                        className="inline-flex items-center gap-2 btn-primary text-lg px-8 py-3"
                    >
                        Find a PT Near Me
                        <ArrowRightIcon className="h-5 w-5" />
                    </a>
                </div>
            </div>

            {/* CTA Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="card-luxury p-8 sm:p-12 text-center bg-gradient-to-br from-emerald-600/10 to-transparent">
                    <h2 className="text-3xl sm:text-4xl font-display font-bold text-luxury-pearl mb-4">
                        Ready to Transform Your Business?
                    </h2>
                    <p className="text-lg text-luxury-pearl/60 mb-8 max-w-2xl mx-auto">
                        Join personal trainers who are already using WorldsHighStreet to streamline their operations and grow their business.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                            onClick={handleGetStartedClick}
                            className="btn-primary text-lg px-8 py-3 w-full sm:w-auto flex items-center justify-center gap-2 group"
                        >
                            View Plans & Pricing
                            <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            onClick={handleLearnMoreClick}
                            className="border border-emerald-600/30 hover:border-emerald-600/60 bg-emerald-600/5 hover:bg-emerald-600/10 text-emerald-300 font-semibold px-8 py-3 rounded-lg transition-all duration-200 w-full sm:w-auto"
                        >
                            Explore Features
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-luxury-graphite/30 mt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <p className="text-center text-luxury-pearl/40 text-sm">
                        © 2026 WorldsHighStreet. Empowering Personal Training Businesses.
                    </p>
                </div>
            </footer>
        </div>
    );
}

export default Home;
